import moment from "moment";
import { IShowRecord } from "@src/types/show";
import { IScheduleBoardState } from "@src/types/schedules";

// Grid geometry shared by the manual board and the read-only master-schedule
// view: a leading week-label column plus 7 day columns (Mon–Sun), 48 weeks tall,
// ruled into 4-week blocks.
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export const WEEK_COUNT = 48;
export const WEEKS_PER_BLOCK = 4;

export const cellId = (dayIndex: number, weekIndex: number): string =>
  `${dayIndex}-${weekIndex}`;

// Each 4-week block maps to a 2-hour time-of-day window (block 0 => 00:00).
export const blockIndexForWeek = (weekIndex: number): number =>
  Math.floor(weekIndex / WEEKS_PER_BLOCK);

// Short block-start label shown sideways in the thin hour-axis column
// (00:00 → "0:00", 02:00 → "2:00", … "22:00"), one per 4-week block.
export const blockStartHourLabel = (weekIndex: number): string =>
  `${blockIndexForWeek(weekIndex) * 2}:00`;

// Full two-hour window a block covers, used as the hour label's tooltip
// (e.g. block 0 => "00:00–02:00").
export const blockTimeRange = (weekIndex: number): string => {
  const startHour = blockIndexForWeek(weekIndex) * 2;
  const pad = (h: number): string => String(h).padStart(2, "0");
  return `${pad(startHour)}:00–${pad(startHour + 2)}:00`;
};

// A user-authored entry that does not exist in the system yet. Kept purely on
// the client (localStorage) alongside the board placements.
export type NoteShow = { id: string; title: string };

// A show auto-placed on the read-only master-schedule view, derived from the
// backend master schedule (not from localStorage placements). `key` is the show
// documentId and is unique within a single cell.
export type MasterGridItem = {
  key: string;
  title: string;
  subtitle?: string;
  state: ShowState;
  count: number;
  // ISO datetime of the master airing that placed this item in the cell. Used by
  // the comparison-view "Apply" action as the slot the board show is scheduled
  // into.
  slotTime?: string;
  // Raw scheduling cadence of the master show, used to gate "Apply" to slots
  // where the board show airs on the same frequency.
  frequency?: string | null;
};

// Anything that can occupy a grid cell: a real show or a custom note-show.
export type PlacedItem =
  | { kind: "show"; show: IShowRecord }
  | { kind: "note"; note: NoteShow };

// Payload emitted when the comparison view's "Apply" action is triggered on a
// slot where the board and master sides differ: schedule `boardItem` into the
// master slot at `slotTime`, replacing the master show identified by
// `masterShowId`.
export type ApplyRequest = {
  cellId: string;
  boardItem: PlacedItem;
  masterTitle: string;
  masterShowId: string;
  slotTime: string;
};

// dnd-kit drag payloads. Inventory items come from the sidebar (and stay there,
// so the same entry can be dropped into many cells); placed items already live
// on the grid.
export type DragData =
  | { type: "inventory-show"; show: IShowRecord }
  | { type: "inventory-note"; note: NoteShow }
  | { type: "placed"; item: PlacedItem; fromCellId: string };

// Auto-derived (New / Regular) or manually overridden (Stopping, or a forced
// New / Regular) lifecycle state that drives a show's text colour on the board.
// Notes have no state.
export type ShowState = "new" | "regular" | "stopping";

// A show is "New" until it has at least one archived playlist; after that it is
// a regular active show. "Stopping" is never auto-assigned — only set via the
// asterisk menu.
export const autoShowState = (archivedCount: number): ShowState =>
  archivedCount < 1 ? "new" : "regular";

// Manual override (if any) wins over the auto state.
export const resolveShowState = (
  override: ShowState | undefined,
  archivedCount: number
): ShowState => override ?? autoShowState(archivedCount);

// Compact next-air date ("MM.DD") pulled from the master schedule for a
// stopping show, or an em dash when the schedule has no upcoming airing for it.
export const formatNextAirDate = (iso: string | null | undefined): string =>
  iso ? moment(iso).format("MM.DD") : "—";

export const placedItemTitle = (item: PlacedItem): string =>
  item.kind === "show" ? item.show.title : item.note.title;

export const placedItemShowId = (item: PlacedItem): string | null =>
  item.kind === "show" ? item.show.documentId : null;

// Serialize the board into the compact, backend-persisted shape. Show cells keep
// only the documentId (rehydrated against live shows on load), notes stay inline.
export const serializeBoard = (
  cells: Record<string, PlacedItem>,
  noteShows: NoteShow[],
  showStates: Record<string, ShowState>
): IScheduleBoardState => {
  const placements: IScheduleBoardState["placements"] = {};
  Object.entries(cells).forEach(([id, item]) => {
    placements[id] =
      item.kind === "show"
        ? { kind: "show", showId: item.show.documentId }
        : { kind: "note", note: item.note };
  });
  return { placements, noteShows, showStates };
};

// Rehydrate a persisted board into live PlacedItems. Show refs resolve against
// the current shows map; a ref to a show that no longer exists is dropped so the
// board never renders a dangling id.
export const deserializeBoard = (
  state: IScheduleBoardState | null | undefined,
  showsById: Record<string, IShowRecord>
): {
  placements: Record<string, PlacedItem>;
  noteShows: NoteShow[];
  showStates: Record<string, ShowState>;
} => {
  const placements: Record<string, PlacedItem> = {};
  Object.entries(state?.placements ?? {}).forEach(([id, ref]) => {
    if (ref.kind === "note") {
      placements[id] = { kind: "note", note: ref.note };
      return;
    }
    const show = showsById[ref.showId];
    if (show) placements[id] = { kind: "show", show };
  });
  return {
    placements,
    noteShows: state?.noteShows ?? [],
    showStates: (state?.showStates ?? {}) as Record<string, ShowState>,
  };
};

// Seed board placements from the auto-placed master schedule. Each cell takes its
// primary (first) master show; a show missing from the shows map still places via
// a minimal {documentId,title} record so nothing is lost when pulling.
export const placementsFromMaster = (
  masterCells: Record<string, MasterGridItem[]>,
  showsById: Record<string, IShowRecord>
): Record<string, PlacedItem> => {
  const out: Record<string, PlacedItem> = {};
  Object.entries(masterCells).forEach(([id, items]) => {
    const first = items[0];
    if (!first) return;
    const show =
      showsById[first.key] ??
      ({ documentId: first.key, title: first.title } as IShowRecord);
    out[id] = { kind: "show", show };
  });
  return out;
};

// Older placements were stored as bare IShowRecord values (one show per cell,
// no notes). Normalise both shapes so a returning user keeps their board.
export const normalizePlacedItem = (value: unknown): PlacedItem | null => {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.kind === "note" && v.note)
    return { kind: "note", note: v.note as NoteShow };
  if (v.kind === "show" && v.show)
    return { kind: "show", show: v.show as IShowRecord };
  if (typeof v.documentId === "string" && typeof v.title === "string")
    return { kind: "show", show: v as unknown as IShowRecord };
  return null;
};
